// The Confidence_Threshold ("Flag fields below this confidence"), shared by the Result_View, the Set_Result_View, the
// Document_Viewer, and the Enlarged_Viewer (enlarge-preview-annotate Req 7.5). It is a module-level store read through
// useSyncExternalStore, so a slider change restyles annotations in the same render. The value is kept in localStorage
// under the same key and with the same parsing as before; a write applies to the in-memory value first, so a blocked
// storage write still takes effect for the life of the tab, with no message.
import { useSyncExternalStore } from 'react';

export const THRESHOLD_KEY = 'bda:confidenceThreshold';
export const DEFAULT_THRESHOLD = 0.8;

type WritableStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** window.localStorage, or null when there is no window or accessing it throws (blocked by policy). */
function browserStorage(): WritableStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The stored threshold (0-1), or DEFAULT_THRESHOLD when absent, invalid, or unreadable. */
export function loadThreshold(storage: Pick<Storage, 'getItem'> | null = browserStorage()): number {
  if (!storage) return DEFAULT_THRESHOLD;
  try {
    const raw = storage.getItem(THRESHOLD_KEY);
    const v = Number(raw);
    // An absent key reads as Number(null) = 0, so require the key before accepting 0 (0 % flags nothing).
    return raw !== null && raw.trim() !== '' && v >= 0 && v <= 1 ? v : DEFAULT_THRESHOLD;
  } catch {
    return DEFAULT_THRESHOLD;
  }
}

let value: number | null = null; // null: not read yet
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

const get = (): number => {
  if (value === null) value = loadThreshold();
  return value;
};

const set = (v: number): void => {
  const changed = get() !== v;
  value = v;
  try {
    browserStorage()?.setItem(THRESHOLD_KEY, String(v));
  } catch {
    // Storage blocked or full: the in-memory value still applies for this tab.
  }
  if (changed) notify();
};

const onStorage = (e: StorageEvent) => {
  // key null means another tab cleared storage.
  if (e.key !== null && e.key !== THRESHOLD_KEY) return;
  const next = loadThreshold();
  if (next !== value) {
    value = next;
    notify();
  }
};

const listen = (on: boolean) => {
  if (typeof window === 'undefined') return;
  if (on) window.addEventListener('storage', onStorage);
  else window.removeEventListener('storage', onStorage);
};

const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0) listen(true);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) listen(false);
  };
};

/** The shared Confidence_Threshold and its setter; every caller re-renders on a change from any of them. */
export function useConfidenceThreshold(): [number, (v: number) => void] {
  const v = useSyncExternalStore(subscribe, get, get);
  return [v, set];
}

/** Tests: forget the in-memory value so the next read comes from localStorage, and stop listening. */
export function resetConfidenceThresholdForTests(): void {
  if (listeners.size > 0) listen(false);
  listeners.clear();
  value = null;
}
